// client/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import './index.css';

// --- Import Onboarding Components ---
import WelcomeModal from './components/onboarding/WelcomeModal';
import OnboardingChecklist from './components/onboarding/OnboardingChecklist';
import { onboardingConfig } from './onboarding/onboardingConfig';

// --- Import Cookie Consent Banner ---
import CookieConsentBanner from './components/CookieConsentBanner';

// --- Import Page Components ---
import HomePage from './pages/HomePage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import SettingsPage from './pages/SettingsPage';
import TemplateListPage from './pages/TemplateListPage';
import TemplateFormPage from './pages/TemplateFormPage';
import ClientListPage from './pages/ClientListPage';
import ClientFormPage from './pages/ClientFormPage';
import ClientOverviewPage from './pages/ClientOverviewPage';
import QuoteListPage from './pages/QuoteListPage';
import QuoteFormPage from './pages/QuoteFormPage';
import PublicQuoteViewPage from './pages/PublicQuoteViewPage';
import InvoiceListPage from './pages/InvoiceListPage';
import InvoiceFormPage from './pages/InvoiceFormPage';
import PublicInvoiceViewPage from './pages/PublicInvoiceViewPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'; // Import the new PrivacyPolicyPage
import TermsOfServicePage from './pages/TermsOfServicePage'; // Import the new TermsOfServicePage
import TradeSpecificLandingPage from './pages/TradeSpecificLandingPage'; // Import Trade-Specific Landing Page
import BlogPage from './pages/BlogPage'; // Import Blog Page
import BlogPostPage from './pages/BlogPostPage'; // Import Blog Post Page
import SupportPage from './pages/SupportPage'; // Import Support Page
import SalesPipelinePage from './pages/SalesPipelinePage'; // Import Sales Pipeline Page
import AppointmentsPage from './pages/AppointmentsPage'; // Import Appointments Page
import EmailTemplatesSettingsPage from './pages/EmailTemplatesSettingsPage'; // Import Email Templates Settings Page
import PublicBookingFormPage from './pages/PublicBookingFormPage'; // Import Public Booking Form Page
import PublicBookingThankYouPage from './pages/PublicBookingThankYouPage'; // Import Public Booking Thank You Page
import FeaturesPage from './pages/FeaturesPage'; // Import Features Page
import PricingPage from './pages/PricingPage'; // Import Pricing Page
import WhyIBuiltThisPage from './pages/WhyIBuiltThisPage'; // Import Why I Built This Page

// --- Import Layout Components ---
import DefaultLayout from './components/DefaultLayout';

// --- AppContent Component ---
// This new component contains the logic for the onboarding UI
const AppContent = () => {
    const { user } = useAuth();
    const [tourState, setTourState] = useState({
        run: false,
        steps: [],
        stepIndex: 0,
    });
    const [onboardingProgress, setOnboardingProgress] = useState({
        // Phase 1
        clientsExist: false,
        quotesExist: false,
        quoteIsCustomized: false,
        quoteIsSent: false,
        settingsAreCustomized: false,
        gmailIsConnected: false,
        // Phase 2
        appointmentIsScheduled: false,
        sectionTemplateExists: false,
        quoteUsesTemplate: false,
        emailTemplatesAreEdited: false,
        proposalIsAccepted: false,
    });

    useEffect(() => {
        const checkOnboardingProgress = async () => {
            if (user && user.onboardingStatus === 'in_progress') {
                try {
                    const { fetchClients } = await import('./api/clients');
                    const { fetchQuotes } = await import('./api/quotes');
                    const { fetchAppointments } = await import('./api/appointments');
                    const { fetchTemplates } = await import('./api/templates');
                    const { getEmailTemplates } = await import('./api/emailTemplates');

                    const clientsResponse = await fetchClients();
                    const quotesResponse = await fetchQuotes();
                    const appointmentsResponse = await fetchAppointments();
                    const templatesResponse = await fetchTemplates();
                    const emailTemplatesResponse = await getEmailTemplates();

                    // Phase 1 Checks
                    const quoteIsSent = quotesResponse.quotes.some(quote => ['sent', 'accepted', 'rejected'].includes(quote.status));
                    const settingsAreCustomized = !!user.companyName;
                    const gmailIsConnected = !!user.isGmailConnected;

                    // Phase 2 Checks
                    const appointmentIsScheduled = appointmentsResponse.appointments && appointmentsResponse.appointments.length > 0;
                    const sectionTemplateExists = templatesResponse.templates && templatesResponse.templates.length > 0;
                    // Check if any section in any quote uses a template
                    const quoteUsesTemplate = quotesResponse.quotes && quotesResponse.quotes.some(quote =>
                        quote.sections && Array.isArray(quote.sections) && quote.sections.some(section => section.templateId || section.template_id)
                    );
                    // Check if any email templates have been customized (is_customized flag)
                    const emailTemplatesAreEdited = emailTemplatesResponse && emailTemplatesResponse.templates && Array.isArray(emailTemplatesResponse.templates) && emailTemplatesResponse.templates.some(template => template.is_customized);
                    const proposalIsAccepted = quotesResponse.quotes && quotesResponse.quotes.some(quote => quote.status === 'accepted');

                    setOnboardingProgress({
                        // Phase 1
                        clientsExist: clientsResponse.clients.length > 0,
                        quotesExist: quotesResponse.quotes.length > 0,
                        quoteIsCustomized: quoteIsSent,
                        quoteIsSent: quoteIsSent,
                        settingsAreCustomized: settingsAreCustomized,
                        gmailIsConnected: gmailIsConnected,
                        // Phase 2
                        appointmentIsScheduled: appointmentIsScheduled,
                        sectionTemplateExists: sectionTemplateExists,
                        quoteUsesTemplate: quoteUsesTemplate,
                        emailTemplatesAreEdited: emailTemplatesAreEdited,
                        proposalIsAccepted: proposalIsAccepted,
                    });
                } catch (error) {
                    console.error('Error checking onboarding progress:', error);
                }
            }
        };

        checkOnboardingProgress();

        // Re-check onboarding progress every 5 seconds to catch updates from other tabs/components
        const interval = setInterval(checkOnboardingProgress, 5000);

        return () => clearInterval(interval);
    }, [user]);

    const completedSteps = onboardingConfig.checklist
        .filter(step => onboardingProgress[step.check])
        .map(step => step.id);

    const handleStartTour = (tourName) => {
        const tour = onboardingConfig.tours[tourName];
        if (tour) {
            setTourState({
                run: true,
                steps: tour,
                stepIndex: 0,
            });
        }
    };

    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;

        if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            setTourState(prev => ({ ...prev, stepIndex: index + (action === ACTIONS.PREV ? -1 : 1) }));
        }
        else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setTourState(prev => ({ ...prev, run: false }));
        }
    };

    return (
        <>
            <CookieConsentBanner />
            {user && user.onboardingStatus === 'in_progress' && <OnboardingChecklist onStepClick={handleStartTour} completedSteps={completedSteps} />}
            <Joyride
                continuous
                callback={handleJoyrideCallback}
                run={tourState.run}
                steps={tourState.steps}
                stepIndex={tourState.stepIndex}
                showProgress
                showSkipButton
            />
            <Routes>
                {/* --- Public Routes --- */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/quotes/public/:publicAccessToken" element={<PublicQuoteViewPage />} />
                <Route path="/invoices/public/:publicAccessToken" element={<PublicInvoiceViewPage />} />
                <Route path="/book/:userSlug" element={<PublicBookingFormPage />} />
                <Route path="/book/:userSlug/thank-you" element={<PublicBookingThankYouPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/estimate-software/:tradeSlug" element={<TradeSpecificLandingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:id" element={<BlogPostPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/why-i-built-this" element={<WhyIBuiltThisPage />} />

                {/* --- Private Routes --- */}
                <Route
                    path="/"
                    element={
                        <PrivateRoute>
                            <DefaultLayout />
                        </PrivateRoute>
                    }
                >
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="profile-settings" element={<ProfileSettingsPage />} />
                    <Route path="templates" element={<TemplateListPage />} />
                    <Route path="templates/new" element={<TemplateFormPage />} />
                    <Route path="templates/edit/:id" element={<TemplateFormPage />} />
                    <Route path="clients" element={<ClientListPage />} />
                    <Route path="clients/new" element={<ClientFormPage />} />
                    <Route path="clients/edit/:id" element={<ClientFormPage />} />
                    <Route path="clients/:id" element={<ClientOverviewPage />} />
                    <Route path="quotes" element={<QuoteListPage />} />
                    <Route path="quotes/new" element={<QuoteFormPage />} />
                    <Route path="quotes/edit/:id" element={<QuoteFormPage />} />
                    <Route path="invoices" element={<InvoiceListPage />} />
                    <Route path="invoices/new" element={<InvoiceFormPage />} />
                    <Route path="invoices/view/:id" element={<Navigate to="/invoices/edit/:id" replace />} />
                    <Route path="invoices/edit/:id" element={<InvoiceFormPage />} />
                    <Route path="pipeline" element={<SalesPipelinePage />} />
                    <Route path="appointments" element={<AppointmentsPage />} />
                    <Route path="email-templates" element={<EmailTemplatesSettingsPage />} />
                    <Route path="support" element={<SupportPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
        </>
    );
}

// --- PrivateRoute Component ---
const PrivateRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

// Main App component
function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppContent />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
